import { Component, DestroyRef, inject, model, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MemberProfileHeaderComponent } from '../../components/member-profile-header/member-profile-header.component';
import { MemberModel } from '../../../../core/models/data/member.model';
import { MemberService } from '../../../../core/services/member.service';
import { MemberUtilities } from '../../../../core/utilities/member.utilities';
import { ProfileConstants } from '../../../../core/constants/profile.constants';
import { MemberProfilePropertiesComponent } from '../../components/member-profile-properties/member-profile-properties.component';
import { MemberProfileLogsComponent } from '../../components/member-profile-logs/member-profile-logs.component';
import { MemberProfileInvoicingComponent } from '../../components/member-profile-invoicing/member-profile-invoicing.component';
import { MemberProfilePaymentHistoryComponent } from '../../components/member-profile-payment-history/member-profile-payment-history.component';
import { MemberProfileCommunicationHistoryComponent } from '../../components/member-profile-communication-history/member-profile-communication-history.component';
import { MemberProfileSettingsComponent } from '../../components/member-profile-settings/member-profile-settings.component';
import { MemberFormModalComponent } from '../../components/member-form-modal/member-form-modal.component';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { MemberStatuses } from '../../../../core/enums/data.enums';
import { FormState } from '../../../../core/enums/forms.enums';
import { MemberConstants } from '../../../../core/constants/constants';
import { GenericMessagingConstants, MembersMessagingConstants } from '../../../../core/constants/messaging.constants';
import { BaseProfilePageClass } from '../../../../core/base-classes/base-profile-page-class';
import { ProfileOrganizationScopeService } from '../../../../core/services/authorization/profile-organization-scope.service';

@Component({
  selector: 'ms-member-profile.page',
  imports: [CommonModule, FontAwesomeModule, MemberProfileHeaderComponent, MemberProfilePropertiesComponent, MemberProfileLogsComponent, MemberProfileInvoicingComponent, MemberProfilePaymentHistoryComponent, MemberProfileCommunicationHistoryComponent, MemberProfileSettingsComponent, MemberFormModalComponent, ConfirmationModalComponent, InfoModalComponent],
  templateUrl: './member-profile.page.html',
  // Scopes every permission check beneath this page to the member's own organization. A member
  // profile is reachable for any organization the viewer belongs to without changing the active one
  // (DEV-2643).
  providers: [ProfileOrganizationScopeService]
})
export class MemberProfilePage extends BaseProfilePageClass implements OnInit {

  public member = model<MemberModel | undefined>(undefined);
  public isLoading = signal<boolean>(false);
  public isSubmitting = signal<boolean | undefined>(undefined);
  public memberId: number | undefined;
  public editFormState = signal<FormState>(FormState.Update);

  @ViewChild('memberProfileConfirmationModalRef') confirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('memberProfileInfoModalRef') infoModalRef!: InfoModalComponent;
  @ViewChild(MemberFormModalComponent) memberFormModalRef!: MemberFormModalComponent;

  private destroyRef = inject(DestroyRef);

  constructor(
    private activatedRoute: ActivatedRoute, 
    private router: Router, 
    private memberService: MemberService,
    private bootstrapService: BootstrapService
  ) {
    super();
  }

  ngOnInit(): void {
    this.navItems.set(ProfileConstants.MEMBER_PROFILE_NAV_ITEMS());
    this.memberId = +(this.activatedRoute.snapshot.paramMap.get('memberId') ?? NaN) || undefined;

    // Balance emails deep link to a tab with ?tab= (DEV-2044). This marks the tab on the unfiltered
    // navItems, so it shows once the member's organization scope resolves, and falls back to the first
    // visible tab if the viewer cannot see it. It must follow navItems.set: the query emits on subscribe.
    this.activatedRoute.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => this.setTabFromUrl(params.get('tab') ?? ''));

    this.loadMemberProfile();
  }

  private async loadMemberProfile(): Promise<void> {
    this.isLoading.set(true);
    this.member.set(undefined);
    this.isEntityOwner.set(false);
    // Cleared before the fetch so org-level permission checks deny while we don't yet know which
    // organization this member belongs to, rather than falling back to the active one.
    this.profileOrganizationScope?.clear();

    try {
      let response = await this.memberService.getMemberById(this.memberId!);

      if (response.success === true && response.data) {
        const cleanedMember = MemberUtilities.withNormalizedPhone(response.data);
        this.profileOrganizationScope?.set(cleanedMember.organizationId);
        this.member.set(cleanedMember);
        this.isEntityOwner.set(MemberUtilities.belongsToCurrentUser(cleanedMember, this.userSessionService));
      } else {
        this.router.navigate(['system', '404']);
        return;
      }
    } catch (error) {
      console.error('Error loading member profile:', error);
      this.router.navigate(['system', '404']);
      return;
    } finally {
      this.isLoading.set(false);
    }
  }

  public onSendInviteRequested(): void {
    this.openSendInviteConfirmationModal();
  }

  public onEditProfileRequested(): void {
    if (this.member()?.status === MemberStatuses.Deleted) return;
    this.memberFormModalRef.openMemberModal();
  }

  public async onEditSuccess(): Promise<void> {
    await this.loadMemberProfile();
  }

  public openSendInviteConfirmationModal(): void {
    const confirmationMessageData = {
      firstName: this.member()?.firstName,
      lastName: this.member()?.lastName,
      expirationDays: MemberConstants.INVITATION_EXPIRATION_DAYS()
    };
    const confirmationMessage = MembersMessagingConstants.MEMBERS_SEND_INVITATION_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);

    this.confirmationModalRef.modalType.set(ModalTypes.Question);
    this.confirmationModalRef.message.set(confirmationMessage);
    this.confirmationModalRef.confirmButtonText.set('Send Invitation');
    this.bootstrapService.manageModal('#memberProfileConfirmationModal', ModalActions.Show);
  }

  public onConfirmationModalConfirmed(): void {
    if (this.memberId) {
      this.sendMemberInvitationEmail();
    } else {
      this.infoModalRef.modalType.set(ModalTypes.Failure);
      this.infoModalRef.message.set(GenericMessagingConstants.GENERIC_ERROR_OCCURRED());
      this.bootstrapService.manageModal('#memberProfileInfoModal', ModalActions.Show);
    }
  }

  public onConfirmationModalCanceled(): void {
    this.bootstrapService.manageModal('#memberProfileConfirmationModal', ModalActions.Hide);
  }

  public async sendMemberInvitationEmail(): Promise<void> {
    this.isSubmitting.set(true);
    const response = await this.memberService.sendMemberInvitationEmail(Number(this.memberId));

    if (response.success === true) {
      const currentMember = this.member();
      if (currentMember) {
        this.member.set({
          ...currentMember,
          status: MemberStatuses.Invited
        });
      }

      this.infoModalRef.modalType.set(ModalTypes.Success);
      const successMessageData = {firstName: this.member()?.firstName, lastName: this.member()?.lastName};
      const successMessage = MembersMessagingConstants.MEMBERS_SEND_INVITATION_SUCCESS().replaceValuesWithObject(successMessageData);
      this.infoModalRef.message.set(successMessage);
    } else {
      this.infoModalRef.modalType.set(ModalTypes.Failure);
      this.infoModalRef.message.set(response.message || MembersMessagingConstants.MEMBERS_SEND_INVITATION_FAILURE());
    }

    this.bootstrapService.manageModal('#memberProfileConfirmationModal', ModalActions.Hide);
    this.bootstrapService.manageModal('#memberProfileInfoModal', ModalActions.Show);

    setTimeout(() => { this.isSubmitting.set(false); }, 1000);
  }

  public onInfoModalConfirmed(): void {
    this.bootstrapService.manageModal('#memberProfileInfoModal', ModalActions.Hide);
  }

  // Patched in place rather than reloaded: onMemberUpdated() below resets to the first tab, which
  // would throw the member off the Settings tab they are standing on. The PATCH returns the whole
  // member, so there is nothing to re-fetch — but it returns it in the API's shape, so it goes through
  // the same normalization the initial load applies rather than straight into the signal.
  public onMemberSettingsUpdated(updated: MemberModel): void {
    this.member.set(MemberUtilities.withNormalizedPhone(updated));
  }

  public async onMemberUpdated(): Promise<void> {
    await this.loadMemberProfile();
    this.setActiveTab(0);
  }
}
